/**
 * Tipos de arquivos de mídia aceitos para upload
 */

// Extensões de áudio aceitas
export const AUDIO_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.ogg',
  '.flac',
  '.aac',
  '.m4a',
  '.wma',
  '.opus',
  '.aiff',
  '.ape'
] as const

// Extensões de vídeo aceitas
export const VIDEO_EXTENSIONS = [
  '.mp4',
  '.avi',
  '.mov',
  '.mkv',
  '.webm',
  '.wmv',
  '.flv',
  '.m4v',
  '.mpeg',
  '.mpg',
  '.3gp',
  '.ts'
] as const

// Todas as extensões de mídia aceitas
export const MEDIA_EXTENSIONS = [...AUDIO_EXTENSIONS, ...VIDEO_EXTENSIONS] as const

// MIME types de áudio aceitos
export const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/ogg',
  'audio/flac',
  'audio/aac',
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a',
  'audio/x-ms-wma',
  'audio/opus',
  'audio/aiff',
  'audio/x-aiff',
  'audio/ape'
] as const

// MIME types de vídeo aceitos
export const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/avi',
  'video/x-msvideo',
  'video/quicktime',
  'video/x-matroska',
  'video/webm',
  'video/x-ms-wmv',
  'video/x-flv',
  'video/x-m4v',
  'video/mpeg',
  'video/3gpp',
  'video/mp2t'
] as const

// Todos os MIME types de mídia aceitos
export const MEDIA_MIME_TYPES = [...AUDIO_MIME_TYPES, ...VIDEO_MIME_TYPES] as const

// Tipos para TypeScript
export type AudioExtension = typeof AUDIO_EXTENSIONS[number]
export type VideoExtension = typeof VIDEO_EXTENSIONS[number]
export type MediaExtension = typeof MEDIA_EXTENSIONS[number]
export type AudioMimeType = typeof AUDIO_MIME_TYPES[number]
export type VideoMimeType = typeof VIDEO_MIME_TYPES[number]
export type MediaMimeType = typeof MEDIA_MIME_TYPES[number]

/**
 * Enum com categorias de mídia
 */
export enum MediaCategory {
  AUDIO = 'audio',
  VIDEO = 'video',
  UNKNOWN = 'unknown'
}

/**
 * Verifica se um arquivo é de mídia válido
 */
export function isValidMediaFile(file: File): boolean {
  // Verifica por MIME type
  if (file.type) {
    const mimeType = file.type.toLowerCase()
    if (mimeType.startsWith('audio/') || mimeType.startsWith('video/')) {
      return true
    }
  }

  // Fallback: verifica por extensão do nome do arquivo
  const fileName = file.name.toLowerCase()
  return MEDIA_EXTENSIONS.some(ext => fileName.endsWith(ext))
}

/**
 * Retorna a categoria de mídia do arquivo
 */
export function getMediaCategory(file: File): MediaCategory {
  const mimeType = file.type?.toLowerCase() || ''
  const fileName = file.name.toLowerCase()

  // Verifica por MIME type primeiro
  if (mimeType.startsWith('audio/')) return MediaCategory.AUDIO
  if (mimeType.startsWith('video/')) return MediaCategory.VIDEO

  // Fallback: verifica por extensão
  if (AUDIO_EXTENSIONS.some(ext => fileName.endsWith(ext))) return MediaCategory.AUDIO
  if (VIDEO_EXTENSIONS.some(ext => fileName.endsWith(ext))) return MediaCategory.VIDEO

  return MediaCategory.UNKNOWN
}

/**
 * Filtra apenas arquivos de mídia válidos de uma FileList
 */
export function filterMediaFiles(files: FileList): File[] {
  return Array.from(files).filter(isValidMediaFile)
}

/**
 * Retorna a string de accept para input file
 */
export function getAcceptString(): string {
  return 'audio/*,video/*'
}

/**
 * Retorna lista de extensões formatada para exibição
 */
export function getAcceptedExtensionsDisplay(): string {
  const audioExts = AUDIO_EXTENSIONS.slice(0, 4).join(', ')
  const videoExts = VIDEO_EXTENSIONS.slice(0, 4).join(', ')
  return `Áudio: ${audioExts}... | Vídeo: ${videoExts}...`
}
